import { Component, HostListener, Inject } from '@angular/core';
import {
  BookingDTO,
  BookingApi,
  OrderApi,
  LogOrderDTO,
  OrderDTO,
  OrderItemDTO,
  ParkingEventDTO,
  PaymentDTO,
  PaymentInvoiceDTO,
  UpdateBookingEventBookingEventTypeEnum,
} from '@lkp/parking-sdk';
import { DateService } from 'src/app/core/services/date.service';
import { Store } from '@ngxs/store';
import { BookingsState } from 'src/app/core/states/bookings/bookings.state';
import { UsersState } from 'src/app/core/states/users/users.state';
import { ActivatedRoute, Router } from '@angular/router';
import {
  CancelBooking,
  GetBooking,
  UpdateParkingStatus,
  UpdateQtyPersonsCheckedOff,
} from 'src/app/core/states/bookings/booking.actions';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent } from 'src/app/components/confirm-dialog/confirm-dialog.component';
import { Subject, takeUntil } from 'rxjs';
import { FormControl } from '@angular/forms';
import { GetOrder } from 'src/app/core/states/orders/orders.action';
import { BOOKING_API, ORDER_API } from 'src/app/core/providers/api.module';
import { BookingPricelistDialogComponent } from 'src/app/components/booking-pricelist-dialog/booking-pricelist-dialog.component';

@Component({
    selector: 'app-booking',
    templateUrl: './booking.component.html',
    styleUrls: ['./booking.component.scss'],
    standalone: false
})
export class BookingComponent {
  booking?: BookingDTO;
  order?: OrderDTO;
  features?: OrderItemDTO[];
  invoice?: PaymentInvoiceDTO;
  emailMsg?: string;
  emailSent?: boolean;
  loading = false;
  paymentMethods: string[] = [];
  unfinishedPayments?: PaymentDTO[];
  orderLog?: LogOrderDTO[];
  parkingEvents?: ParkingEventDTO[];
  isCompactView = false;

  pickedUpCheckbox = new FormControl<boolean>(false);
  droppedOffCheckbox = new FormControl<boolean>(false);
  parkingEntryCheckbox = new FormControl<boolean>(false);
  parkingExitCheckbox = new FormControl<boolean>(false);
  isDriverMode = false;
  hasCarwash = false;
  hasWorkshop = false;
  hasCharging = false;
  routeBookingId?: number;

  readonly onDestroy$ = new Subject<void>();

  constructor(
    readonly dateService: DateService,
    readonly store: Store,
    @Inject(BOOKING_API) readonly bookingApi: BookingApi,
    @Inject(ORDER_API) readonly orderApi: OrderApi,
    readonly router: Router,
    readonly route: ActivatedRoute,
    readonly dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.updateCompactView();

    /* Fetch the booking matching the route id (or the booking already in
       state for the plain /bokning route) so the page always shows fresh data */
    this.route.paramMap
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((params) => {
        this.routeBookingId = Number(params.get('id')) || undefined;

        if (
          this.routeBookingId &&
          this.booking?.bookingId !== this.routeBookingId
        ) {
          this.booking = undefined;
        }

        const bookingId =
          this.routeBookingId ??
          this.store.selectSnapshot(BookingsState.currentBooking)?.bookingId;

        if (bookingId) {
          this.store.dispatch(new GetBooking(bookingId));
        }
      });

    this.store
      .select(UsersState.driverMode)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((dm) => (this.isDriverMode = dm));

    this.store
      .select(BookingsState.currentBooking)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((v) => {
        /* Ignore state updates for a different booking while the route's
           booking is being fetched */
        if (v && (!this.routeBookingId || v.bookingId === this.routeBookingId)) {
          const isNewBooking = this.booking?.bookingId !== v.bookingId;
          this.booking = v;
          if (this.booking) {
            this.getOrderFromBookingId(this.booking.bookingId);
            if (isNewBooking) {
              this.getParkingEvents();
            }

            // Set picked/dropped off and entry/exit checkboxes
            this.pickedUpCheckbox.setValue(
              this.booking.qtyPersonsPickedUp !== undefined &&
                this.booking.qtyPersonsPickedUp === this.booking.qtyPersons
            );
            this.droppedOffCheckbox.setValue(
              this.booking.qtyPersonsDroppedOff !== undefined &&
                this.booking.qtyPersonsDroppedOff === this.booking.qtyPersons
            );
            this.parkingEntryCheckbox.setValue(
              this.booking.parkingEntry !== undefined &&
                !!this.booking.parkingEntry
            );
            this.parkingExitCheckbox.setValue(
              this.booking.parkingExit !== undefined &&
                !!this.booking.parkingExit
            );

            this.updateHighlightFlags();
          }
        } else if (!v) {
          console.error('No booking found');
        }
      });

    this.emailMsg = undefined;
    this.emailSent = undefined;

    /* Listen to dropped off changes */
    this.droppedOffCheckbox.valueChanges
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((value) => {
        value !== null && this.handleParkingStatusChange(value, 'droppedOff');
      });

    /* Listen to picked up changes */
    this.pickedUpCheckbox.valueChanges
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((value) => {
        value !== null && this.handleParkingStatusChange(value, 'pickedUp');
      });

    /* Listen to entry changes */
    this.parkingEntryCheckbox.valueChanges
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((value) => {
        /* Check if value has changed */
        const preValue = !!this.booking?.parkingEntry;
        value !== null &&
          preValue !== value &&
          this.handleParkingStatusChange(value, 'entry');
      });

    /* Listen to exit changes */
    this.parkingExitCheckbox.valueChanges
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((value) => {
        /* Check if value has changed */
        const preValue = !!this.booking?.parkingExit;
        value !== null &&
          preValue !== value &&
          this.handleParkingStatusChange(value, 'exit');
      });
  }

  handleParkingStatusChange(
    value: boolean,
    type: 'droppedOff' | 'pickedUp' | 'entry' | 'exit'
  ): void {
    const qtyPersonChecked =
      type === 'droppedOff'
        ? this.booking?.qtyPersonsDroppedOff
        : this.booking?.qtyPersonsPickedUp;

    const bookingId = this.booking?.bookingId;

    if (value === true) {
      if (type === 'droppedOff' || type === 'pickedUp') {
        if (
          this.booking?.qtyPersons !== qtyPersonChecked &&
          bookingId &&
          this.booking
        ) {
          this.booking.qtyPersons !== undefined &&
            this.store
              .dispatch(
                new UpdateQtyPersonsCheckedOff(
                  type === 'droppedOff' ? 'departure' : 'arrival',
                  bookingId,
                  this.booking.qtyPersons
                )
              )
              .pipe(takeUntil(this.onDestroy$))
              .subscribe({
                next: () => {},
                error: () => {
                  console.error('Error updating qty persons checked off');
                },
              });
        }
      } else if (type === 'entry' || type === 'exit') {
        if (bookingId && this.booking) {
          this.store
            .dispatch(
              new UpdateParkingStatus(
                bookingId,
                value,
                type === 'entry'
                  ? UpdateBookingEventBookingEventTypeEnum.ParkingEntry
                  : UpdateBookingEventBookingEventTypeEnum.ParkingExit
              )
            )
            .pipe(takeUntil(this.onDestroy$))
            .subscribe({
              next: () => {
                this.getParkingEvents();
              },
              error: (error) => {
                console.error('Error updating parking status: ', error);
              },
            });
        }
      }
    } else {
      /* Confirm deselect */
      if (
        ((type === 'droppedOff' || type === 'pickedUp') &&
          this.booking?.qtyPersons !== undefined &&
          this.booking.qtyPersons === qtyPersonChecked) ||
        type === 'entry' ||
        type === 'exit'
      ) {
        let message = '';
        switch (type) {
          case 'droppedOff':
            message = `Bokningen kommer stå som ej lämnade.`;
            break;
          case 'pickedUp':
            message = `Bokningen kommer stå som ej hämtade.`;
            break;
          case 'entry':
            message = `Bokningen kommer stå som ej anlänt P.`;
            break;
          case 'exit':
            message = `Bokningen kommer stå som ej lämnat P.`;
            break;
        }

        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
          data: {
            title: `Är du säker på att du vill ändra status?`,
            message: [message],
            yesBtnText: 'Ja',
          },
        });

        dialogRef
          .afterClosed()
          .pipe(takeUntil(this.onDestroy$))
          .subscribe((res) => {
            /* Update parking status */
            if (type === 'entry' || type === 'exit') {
              if (res) {
                this.booking?.bookingId &&
                  this.store
                    .dispatch(
                      new UpdateParkingStatus(
                        this.booking.bookingId,
                        false,
                        type === 'entry'
                          ? UpdateBookingEventBookingEventTypeEnum.ParkingEntry
                          : UpdateBookingEventBookingEventTypeEnum.ParkingExit
                      )
                    )
                    .pipe(takeUntil(this.onDestroy$))
                    .subscribe({
                      next: () => {
                        this.getParkingEvents();
                      },
                      error: () => {
                        console.error('Error updating parking status');
                      },
                    });
              } else {
                type === 'entry'
                  ? this.parkingEntryCheckbox.setValue(true)
                  : this.parkingExitCheckbox.setValue(true);
              }
            } else if (type === 'droppedOff' || type === 'pickedUp') {
              /* Update qty persons */
              if (res) {
                this.booking?.bookingId &&
                  this.store
                    .dispatch(
                      new UpdateQtyPersonsCheckedOff(
                        type === 'droppedOff' ? 'departure' : 'arrival',
                        this.booking.bookingId,
                        0
                      )
                    )
                    .pipe(takeUntil(this.onDestroy$))
                    .subscribe({
                      next: () => {},
                      error: () => {
                        console.error('Error updating qty persons checked off');
                      },
                    });
              } else {
                type === 'droppedOff'
                  ? this.droppedOffCheckbox.setValue(true)
                  : this.pickedUpCheckbox.setValue(true);
              }
            }
          });
      }
    }
  }

  getParkingEvents(): void {
    this.booking?.bookingId &&
      this.bookingApi
        .getParkingEvents({ bookingId: this.booking?.bookingId! })
        .pipe(takeUntil(this.onDestroy$))
        .subscribe((events) => {
          this.parkingEvents = events;
        });
  }

  updateHighlightFlags(): void {
    const mainFeatureTypes = new Set(
      (this.features || [])
        .map((f) => f.feature?.mainFeature?.mainFeatureType)
        .filter((t): t is string => !!t)
    );

    const bookingAny = this.booking as any;

    this.hasCarwash = !!bookingAny?.carWash || mainFeatureTypes.has('CAR_WASH');
    this.hasWorkshop =
      !!bookingAny?.tireRepair || mainFeatureTypes.has('WORKSHOP');
    this.hasCharging = mainFeatureTypes.has('GARAGE');
  }

  getOrderFromBookingId(id?: number): void {
    id &&
      this.orderApi
        .getOrdersAdmin({
          size: 1,
          bookingId: id,
        })
        .pipe(takeUntil(this.onDestroy$))
        .subscribe((order) => {
          this.order = order.content?.[0];
          this.features = this.order?.orderItems?.filter(
            (item) =>
              item.orderItemType === 'FEATURE' &&
              item.booking?.bookingId &&
              item.booking.bookingId === id
          );

          this.updateHighlightFlags();

          this.invoice = this.order?.payments?.find(
            (payment) => payment.paymentInvoice && payment.paymentInvoice
          )?.paymentInvoice;
          this.paymentMethods = [
            ...new Set(
              this.order!.payments?.map((item) => item.paymentMethod!.label!)
            ),
          ];

          /* Get order history */
          this.order?.orderId &&
            this.orderApi
              .getOrderLog({ orderId: this.order.orderId })
              .pipe(takeUntil(this.onDestroy$))
              .subscribe((orderLog) => {
                this.orderLog = orderLog;
              });

          /* Check if order has unfinished payments */
          this.hasUnfinishedPayments();
        });
  }

  @HostListener('window:resize')
  onResize(): void {
    this.updateCompactView();
  }

  updateCompactView(): void {
    this.isCompactView = window.innerWidth <= 800;
  }

  formatDate(date: string | Date | undefined): string {
    return date ? this.dateService.formatDate(date) : '';
  }

  formatTime(date: string | Date | undefined): string {
    return date ? this.dateService.formatTime(date) : '';
  }

  hasUnfinishedPayments(): void {
    this.order?.orderId &&
      this.orderApi
        .getUnfinishedPayments({ orderId: this.order?.orderId! })
        .pipe(takeUntil(this.onDestroy$))
        .subscribe({
          next: (value) => {
            this.unfinishedPayments = value;
          },
          error: (error) =>
            console.error('Error fetching unfinished payments: ', error),
        });
  }

  navBack() {
    this.router.navigate(['hem']);
  }

  editBooking() {
    // open single page view
    this.router.navigate(['redigera-bokning', this.booking?.bookingId]);
  }

  deleteBooking() {
    let bookingItem = this.order?.orderItems?.find(
      (i) => i.booking?.bookingId == this.booking?.bookingId
    );

    /* Check if all associated order items are refunded/not paid */
    const allAssociatedItemsRefunded = this.order?.orderItems
      ?.filter(
        (item) =>
          item.orderItemType === 'FEATURE' &&
          item.booking?.bookingId === this.booking?.bookingId
      )
      .every((featureItem) => featureItem.amountPaid === 0);

    /* Ask to delete if nothing left to refund */
    if (bookingItem?.amountPaid === 0 && allAssociatedItemsRefunded) {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        data: {
          title: 'Är du säker på att du vill ta bort bokningen?',
          message: [
            'Bokningen kommer att försvinna från hämt- och bokningslistan, men kommer fortfarande att synas i rapporter.',
          ],
          yesBtnText: 'Avboka',
        },
      });

      dialogRef
        .afterClosed()
        .pipe(takeUntil(this.onDestroy$))
        .subscribe((res) => {
          if (res) {
            this.store
              .dispatch(new CancelBooking(this.booking?.bookingId!))
              .pipe(takeUntil(this.onDestroy$))
              .subscribe(() => {
                this.router.navigate(['/hem']);
              });
          }
        });
    } else {
      this.dialog.open(ConfirmDialogComponent, {
        data: {
          title: 'Bokningen och/eller eventuell tvätt är inte helt återbetald!',
          message: [
            'För att kunna avboka måste bokning samt eventuell tvätt vara återbetalt.',
          ],
          yesBtnText: 'OK',
        },
      });
    }
  }

  showPricelist(): void {
    if (this.booking?.bookingId !== undefined) {
      this.dialog.open(BookingPricelistDialogComponent, {
        data: { bookingId: this.booking.bookingId },
        maxWidth: '95vw',
      });
    }
  }

  isCancelled(): boolean {
    return this.booking?.bookingStatus === 'CANCELLED' || false;
  }

  sendEmail() {
    const confDialog = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: `Är du säker på att du vill skicka bokningsbekräftelse till ${this.booking?.name}?`,
        message: [
          `Kunden kommer att få en bokningsbekräftelse till den angivna e-postadressen och det angivna telefonnumret i bokningen.`,
        ],
        yesBtnText: 'Skicka',
      },
    });

    confDialog
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((result) => {
        if (result) {
          if (this.booking?.bookingId) {
            this.loading = true;
            this.emailMsg = undefined;
            this.bookingApi
              .sendBookingConfirmationAdmin({
                bookingId: this.booking?.bookingId!,
              })
              .pipe(takeUntil(this.onDestroy$))
              .subscribe({
                next: () => {
                  this.emailSent = true;
                  this.emailMsg = 'Bokningsbekräftelse skickat!';
                  this.loading = false;
                },
                error: () => {
                  this.emailSent = false;
                  this.emailMsg =
                    'Det gick inte att skicka bokningsbekräftelsen, kontrollera e-postadressen och telefonnumret.';
                  this.loading = false;
                },
              });
          } else {
            this.emailSent = false;
            this.emailMsg =
              'Det gick inte att skicka bokningsbekräftelsen, ladda om sidan och försök igen.';
          }
        }
      });
  }

  showOrder(id?: number) {
    if (id !== undefined) {
      this.store
        .dispatch(new GetOrder(id))
        .pipe(takeUntil(this.onDestroy$))
        .subscribe(() => {
          this.router.navigate([`/order/`, id]);
        });
    }
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
}
